import CryptoJS from "crypto-js"; // Add this import
import {
	addDoc,
	collection,
	doc,
	getDoc,
	getDocs,
	increment,
	updateDoc,
} from "firebase/firestore";
import React, { useEffect, useState } from "react";
import Joyride from "react-joyride";
import { useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserContext"; // Assuming you have a UserContext to get the current user
import { db } from "../firebaseConfig"; // Ensure this path is correct
import Tooltip from "./Tooltip"; // Ensure Tooltip is imported

const JournalHome = ({ isFreeAccess = false }) => {
	const navigate = useNavigate();
	const { user, encryptionKey } = useUser(); // Get the current user
	const [pastEntries, setPastEntries] = useState([]);
	const [runTour, setRunTour] = useState(false);

	const JournalSteps = [
		{
			target: ".journal-home-section",
			content:
				"Welcome to your journalling space! Here you can see all your past entries. You can click on any entry to edit it.",
			placement: "center",
		},
		{
			target: ".journal-add-entry-button",
			content: "Let's add your first journal entry!",
			placement: "top",
		},
	];

	useEffect(() => {
		const setDemoState = async () => {
			const userRef = doc(db, "Users", user.uid);
			const userDoc = await getDoc(userRef);
			if (userDoc.exists()) {
				const userData = userDoc.data();
				if (!userData.journalHomeDemo) {
					setRunTour(true);
					await updateDoc(userRef, { journalHomeDemo: true });
				}
			}
		};

		const fetchEntries = async () => {
			try {
				if (!user) return; // Ensure user is available
				const entriesCollection = collection(
					db,
					"Users",
					user.uid,
					"entries"
				);
				const entriesSnapshot = await getDocs(entriesCollection);
				const entriesList = entriesSnapshot.docs.map((doc) => {
					const data = doc.data();
					// Decrypt the title
					const decryptedTitle = CryptoJS.AES.decrypt(
						data.title,
						encryptionKey
					).toString(CryptoJS.enc.Utf8);
					return {
						id: doc.id,
						title: decryptedTitle,
						createdAt: data.createdAt,
					};
				});
				setPastEntries(entriesList);
			} catch (error) {
				console.error("Error fetching journal entries:", error);
			}
		};

		setDemoState();
		fetchEntries();
	}, [user]);

	const addNewEntry = async () => {
		try {
			if (!user) return;

			// If using free access, reduce the count
			if (isFreeAccess) {
				const userRef = doc(db, "Users", user.uid);
				await updateDoc(userRef, {
					freeEntriesLeft: increment(-1),
				});
			}

			const newEntry = {
				createdAt: new Date(),
				title: CryptoJS.AES.encrypt("", encryptionKey).toString(),
				body: CryptoJS.AES.encrypt("", encryptionKey).toString(),
			};
			const docRef = await addDoc(
				collection(db, "Users", user.uid, "entries"),
				newEntry
			);
			setPastEntries([{ id: docRef.id, ...newEntry }, ...pastEntries]);
			navigate(`/journal/${docRef.id}`);
		} catch (error) {
			console.error("Error adding new journal entry:", error);
		}
	};

	return (
		<div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 journal-home-section">
			<Joyride
				steps={JournalSteps}
				run={runTour}
				continuous={true}
				showSkipButton={true}
				showProgress={false}
				styles={{
					options: {
						primaryColor: "#60a5fa",
						textColor: "#374151",
					},
					buttonClose: {
						display: "none",
					},
				}}
				locale={{
					last: "Start my writing journey!",
				}}
			/>
			<div className="w-full max-w-6xl h-[calc(var(--vh,1vh)*85)] backdrop-blur-lg rounded-3xl shadow-xl overflow-auto flex bg-white bg-opacity-90">
				<div className="flex-1 p-6">
					<div className="flex justify-center items-center mb-4 py-4">
						<h1 className="text-3xl font-light text-gray-800">
							Journal
						</h1>
						<Tooltip text="Add new entry">
							<button
								onClick={addNewEntry}
								className="ml-5 bg-blue-500 text-xl text-white px-2 rounded-full hover:bg-blue-600 transition-all duration-300 journal-add-entry-button">
								+
							</button>
						</Tooltip>
					</div>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 journal-entry-grid">
						{pastEntries.map((entry) => (
							<div
								key={entry.id}
								className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-all duration-300"
								onClick={() =>
									navigate(`/journal/${entry.id}`)
								}>
								<div className="flex justify-between items-center mb-3">
									<span className="text-sm font-light text-blue-600">
										{entry.createdAt?.toDate instanceof
										Function
											? entry.createdAt
													.toDate()
													.toLocaleDateString(
														"en-US",
														{
															weekday: "long",
															year: "numeric",
															month: "long",
															day: "numeric",
														}
													)
											: "No date available"}
									</span>
								</div>
								<h2 className="text-lg font-light text-gray-800">
									{entry.title}
								</h2>
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	);
};

export default JournalHome;
